import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, Users, Clock, AlertTriangle, ArrowUpRight, Camera } from 'lucide-react';
import { getAllFirmStats, saListMrrSnapshots, saRecordMrrSnapshot } from '../../lib/db';
import { formatMoney, formatDate } from '../../lib/formatters';
import { PLANS } from '../../lib/plans';
import { useRoute } from '../../lib/router';
import toast from '../../lib/toast';

function planPrice(key) {
  // Treat trial/ai/null specially; everything else comes from PLANS.
  if (!key || key === 'trial' || key === 'enterprise' || key === 'ai') return 0;
  return PLANS[key]?.priceGhs || 0;
}

function planLabel(key) {
  return PLANS[key]?.name || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—');
}

function trialDaysLeft(trialEndsAt) {
  if (!trialEndsAt) return null;
  return Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000);
}

const KPI = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
    <div className={`inline-flex p-2 rounded-xl mb-3 ${color}`}>
      <Icon size={18} className="text-white" />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-slate-400 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

function MrrChart({ snapshots, height = 160 }) {
  // Aggregate to the latest snapshot per day, then render a single area line
  // for MRR. Pure-SVG so no chart-library dependency.
  const points = useMemo(() => {
    if (!snapshots?.length) return [];
    const byDay = new Map();
    for (const s of snapshots) {
      const day = new Date(s.captured_at).toISOString().slice(0, 10);
      byDay.set(day, s); // last write per day wins (snapshots are time-ordered)
    }
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, s]) => ({ day, mrr: Number(s.mrr_ghs || 0), active: s.active_firms }));
  }, [snapshots]);

  if (points.length < 2) {
    return (
      <div className="h-40 flex items-center justify-center text-sm text-slate-500">
        Not enough snapshots yet — take a few to see the trend.
      </div>
    );
  }

  const w = 720;
  const h = height;
  const pad = { top: 12, right: 16, bottom: 28, left: 56 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;

  const maxMrr = Math.max(...points.map((p) => p.mrr), 1);
  const minMrr = 0;
  const x = (i) => pad.left + (i / Math.max(1, points.length - 1)) * innerW;
  const y = (v) => pad.top + innerH - ((v - minMrr) / Math.max(1, maxMrr - minMrr)) * innerH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.mrr).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  // 4 gridline ticks
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    v: minMrr + (maxMrr - minMrr) * t,
    yp: pad.top + innerH - t * innerH,
  }));

  const xLabels = points.length > 7
    ? [0, Math.floor(points.length / 2), points.length - 1]
    : points.map((_, i) => i);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" className="w-full">
      <defs>
        <linearGradient id="mrr-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={pad.left} y1={t.yp} x2={w - pad.right} y2={t.yp} stroke="#1e293b" />
          <text x={pad.left - 6} y={t.yp + 3} textAnchor="end" fontSize="10" fill="#64748b">
            {formatMoney(Math.round(t.v))}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#mrr-grad)" />
      <path d={line} fill="none" stroke="#10b981" strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.mrr)} r={1.8} fill="#10b981" />
      ))}
      {xLabels.map((i) => (
        <text key={i} x={x(i)} y={h - 8} textAnchor="middle" fontSize="10" fill="#64748b">
          {points[i].day.slice(5)}
        </text>
      ))}
    </svg>
  );
}

function PlanBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-400 tabular-nums">{count} ({pct}%)</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function SuperAdminRevenuePage() {
  const [firms,   setFirms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [snapshots,  setSnapshots]  = useState([]);
  const [chartDays,  setChartDays]  = useState(90);
  const [snapshotting, setSnapshotting] = useState(false);
  const [, navigate] = useRoute();

  useEffect(() => {
    getAllFirmStats().then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load firms');
      else setFirms(data || []);
      setLoading(false);
    });
  }, []);

  const loadSnapshots = (days) => {
    saListMrrSnapshots(days).then(({ data }) => {
      setSnapshots(Array.isArray(data) ? data : []);
    });
  };
  useEffect(() => { loadSnapshots(chartDays); }, [chartDays]);

  const takeSnapshot = async () => {
    setSnapshotting(true);
    const { error: err } = await saRecordMrrSnapshot('manual');
    setSnapshotting(false);
    if (err) { toast.error(err.message || 'Could not record snapshot'); return; }
    toast.success('Snapshot recorded.');
    loadSnapshots(chartDays);
  };

  const stats = useMemo(() => {
    const now = Date.now();
    const monthAgo = now - 30 * 86400000;

    const active    = firms.filter((f) => f.subscription_status === 'active'    && !f.suspended_at);
    const trial     = firms.filter((f) => f.subscription_status === 'trial'     && !f.suspended_at);
    const suspended = firms.filter((f) => f.suspended_at || f.subscription_status === 'suspended');
    const cancelled = firms.filter((f) => f.subscription_status === 'cancelled');

    // MRR — sum of plan prices for active paying firms only.
    const mrr = active.reduce((sum, f) => sum + planPrice(f.subscription_plan), 0);
    const arr = mrr * 12;

    const newThisMonth = firms.filter((f) => new Date(f.created_at).getTime() >= monthAgo).length;

    // Plan distribution across all firms (including trial)
    const planCounts = {};
    for (const f of firms) {
      const k = f.subscription_plan || 'unknown';
      planCounts[k] = (planCounts[k] || 0) + 1;
    }

    const expiringSoon = trial
      .map((f) => ({ ...f, _days: trialDaysLeft(f.trial_ends_at) }))
      .filter((f) => f._days !== null && f._days <= 7)
      .sort((a, b) => a._days - b._days);

    const recentlySuspended = suspended
      .filter((f) => f.suspended_at)
      .sort((a, b) => new Date(b.suspended_at) - new Date(a.suspended_at))
      .slice(0, 10);

    return {
      active, trial, suspended, cancelled,
      mrr, arr, newThisMonth, planCounts,
      expiringSoon, recentlySuspended,
    };
  }, [firms]);

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;
  if (error)   return <div className="text-center py-20 text-red-400">{error}</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">Revenue & Subscriptions</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          MRR estimated from plan prices · {formatMoney(stats.mrr)} active / month
        </p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label="MRR (active)" value={formatMoney(stats.mrr)} sub={`ARR ${formatMoney(stats.arr)}`}  color="bg-emerald-600" />
        <KPI icon={Users}      label="Active paying" value={stats.active.length}   sub={`${stats.trial.length} on trial`} color="bg-indigo-600" />
        <KPI icon={ArrowUpRight} label="New this month" value={stats.newThisMonth} sub="(last 30 days)" color="bg-blue-600" />
        <KPI icon={AlertTriangle} label="Suspended" value={stats.suspended.length} sub={`${stats.cancelled.length} cancelled`} color="bg-red-600" />
      </div>

      {/* MRR over time */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald-400" />
            MRR over time
          </h2>
          <div className="flex items-center gap-2">
            {[30, 90, 180, 365].map((d) => (
              <button
                key={d}
                onClick={() => setChartDays(d)}
                className={`px-2.5 py-1 rounded-lg text-xs transition ${
                  chartDays === d ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={takeSnapshot}
              disabled={snapshotting}
              className="ml-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50"
              title="Capture an MRR snapshot from the current state"
            >
              <Camera size={11} /> {snapshotting ? 'Snapping…' : 'Snapshot now'}
            </button>
          </div>
        </div>
        <MrrChart snapshots={snapshots} />
        <p className="text-xs text-slate-500">
          Snapshots are captured automatically on every plan change and bulk plan update. Use “Snapshot now” to add a manual data point any time.
        </p>
      </div>

      {/* Plan distribution */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Plan distribution</h2>
          <span className="text-xs text-slate-500">{firms.length} firms total</span>
        </div>
        <PlanBar label="Pro"        count={stats.planCounts.pro        || 0} total={firms.length} color="bg-indigo-500" />
        <PlanBar label="Basic"      count={stats.planCounts.basic      || 0} total={firms.length} color="bg-blue-500" />
        <PlanBar label="Enterprise" count={(stats.planCounts.ai || 0) + (stats.planCounts.enterprise || 0)} total={firms.length} color="bg-purple-500" />
        <PlanBar label="Trial"      count={stats.planCounts.trial      || 0} total={firms.length} color="bg-amber-500" />
        {stats.planCounts.unknown ? (
          <PlanBar label="Unknown" count={stats.planCounts.unknown} total={firms.length} color="bg-slate-500" />
        ) : null}
      </div>

      {/* Two-column lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trials expiring */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Clock size={14} className="text-amber-400" />
              Trials expiring soon
            </h2>
            <span className="text-xs text-slate-500">{stats.expiringSoon.length}</span>
          </div>
          {stats.expiringSoon.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-500">No trials expiring in the next 7 days.</p>
          ) : (
            <ul className="divide-y divide-slate-800">
              {stats.expiringSoon.map((f) => (
                <li
                  key={f.firm_id}
                  onClick={() => navigate(`/superadmin/firms/${f.firm_id}`)}
                  className="px-5 py-3 cursor-pointer hover:bg-slate-800/50 transition flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-medium text-white">{f.firm_name}</p>
                    <p className="text-xs text-slate-500">{f.firm_email || '—'}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${f._days <= 0 ? 'bg-red-900/40 text-red-300' : f._days <= 3 ? 'bg-amber-900/40 text-amber-300' : 'bg-slate-700 text-slate-300'}`}>
                    {f._days <= 0 ? 'expired' : `${f._days}d left`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recently suspended */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-400" />
              Recently suspended
            </h2>
            <span className="text-xs text-slate-500">{stats.recentlySuspended.length}</span>
          </div>
          {stats.recentlySuspended.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-500">No firms suspended.</p>
          ) : (
            <ul className="divide-y divide-slate-800">
              {stats.recentlySuspended.map((f) => (
                <li
                  key={f.firm_id}
                  onClick={() => navigate(`/superadmin/firms/${f.firm_id}`)}
                  className="px-5 py-3 cursor-pointer hover:bg-slate-800/50 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-white">{f.firm_name}</p>
                      <p className="text-xs text-slate-500">{planLabel(f.subscription_plan)} · {formatDate(f.suspended_at)}</p>
                    </div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-red-900/40 text-red-300 font-medium">Suspended</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
